/**
 * RackPortsTab — Interfaces & Power Ports tab in the left inspector.
 * Interactive: click port to toggle status (Free / Connected / Reserved).
 * SRP: displays port matrix for the selected device with Trace and status actions.
 */
import { useState, useRef, useEffect } from 'react';
import { Zap, Wifi, Shield, Circle, ChevronDown } from 'lucide-react';
import { api } from '../../utils/api';

interface Port {
  id: string | number;
  name: string;
  port_type?: string;
  status?: string;
  connected_cable_id?: string | null;
  [key: string]: any;
}

interface RackPortsTabProps {
  selectedEq: { id: string | number; name: string } | null;
  ports: Port[];
  onTrace: (portId: string | number) => void;
  onPortStatusChange?: () => void;
  highlightPortId?: string | number | null;
}

// --- Helpers ---

const POWER_TYPES = new Set(['C13', 'C14', 'C19', 'C20', 'NEMA-5-15', 'power']);
const MGMT_TYPES  = new Set(['iDRAC', 'iLO', 'IPMI', 'management', 'RJ11', 'USB-A']);

function getPortCategory(port: Port): 'power' | 'mgmt' | 'data' {
  const t = (port.port_type || '').toLowerCase();
  if (POWER_TYPES.has(port.port_type || '') || t.includes('c13') || t.includes('c19') || t === 'power') return 'power';
  if (MGMT_TYPES.has(port.port_type || '') || t.includes('idrac') || t.includes('ilo') || t.includes('ipmi') || t === 'management') return 'mgmt';
  return 'data';
}

function getPortStatus(port: Port): 'connected' | 'reserved' | 'free' {
  if (port.status === 'Connected' || port.connected_cable_id) return 'connected';
  if (port.status === 'Reserved') return 'reserved';
  return 'free';
}

const STATUS_STYLES = {
  connected: 'bg-emerald-950/40 border-emerald-700/60 text-emerald-300',
  reserved:  'bg-amber-950/40  border-amber-700/60  text-amber-300',
  free:      'bg-slate-900     border-slate-800     text-slate-400',
};

const CATEGORY_ICON = {
  power: <Zap   className="w-3 h-3 text-amber-400" />,
  mgmt:  <Shield className="w-3 h-3 text-purple-400" />,
  data:  <Wifi  className="w-3 h-3 text-cyan-400" />,
};

// --- Port status overlay ---

interface PortOverlayProps {
  port: Port;
  onClose: () => void;
  onSaved: () => void;
  onTrace: (portId: string | number) => void;
}

function PortOverlay({ port, onClose, onSaved, onTrace }: PortOverlayProps) {
  const [status, setStatus] = useState<string>(port.status || 'Free');
  const [note, setNote] = useState<string>(port.notes || '');
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const save = async (newStatus?: string) => {
    const finalStatus = newStatus || status;
    setSaving(true);
    try {
      await api.patch(`/api/equipment/ports/${port.id}`, { status: finalStatus, notes: note });
      onSaved();
      onClose();
    } catch (e) {
      console.error('Port update failed', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={ref}
      className="absolute z-50 top-full left-0 mt-1 w-52 bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-3 text-xs space-y-2"
      onClick={e => e.stopPropagation()}
    >
      <div className="font-bold text-slate-200 flex justify-between">
        <span>{port.name}</span>
        <span className="text-slate-500">{port.port_type}</span>
      </div>

      {/* Status selector —
           Проблема 10 (Split Brain): статус "Connected" должен зависеть толькоот физического кабеля.
           Если connected_cable_id есть — показываем ридо чтения (нельзя снять через UI).
           Если нет — доступны Free/Reserved. Ручной "Connected" нет — статус идёт из графа коммутаций. */}
      <div className="space-y-1">
        {port.connected_cable_id ? (
          <div className="flex items-center gap-2 px-2 py-1.5 bg-emerald-950/40 border border-emerald-700/60 rounded text-emerald-300 text-xs">
            <span>🔗</span>
            <span>Подключён (кабель #{String(port.connected_cable_id).slice(0, 8)})</span>
          </div>
        ) : (
          <select
            value={status}
            onChange={e => {
              setStatus(e.target.value);
              save(e.target.value);
            }}
            className="w-full bg-slate-800 border border-slate-700 text-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-cyan-500 cursor-pointer"
          >
            <option value="Free">Свободен</option>
            <option value="Reserved">Зарезервирован</option>
          </select>
        )}
      </div>

      {/* Note — context for Reserved or manual Connected */}
      {(status === 'Connected' || status === 'Reserved') && (
        <div className="space-y-1">
          <input
            type="text"
            placeholder={status === 'Connected' ? 'Куда подключён: устройство / порт (напр. Switch-01 / gi0/1)' : 'Причина резерва / описание...'}
            value={note}
            onChange={e => setNote(e.target.value)}
            className={`w-full rounded px-2 py-1 text-slate-200 placeholder-slate-600 focus:outline-none ${
              status === 'Connected'
                ? 'bg-slate-800 border border-emerald-700/50 focus:border-emerald-600'
                : 'bg-slate-800 border border-amber-700/50 focus:border-amber-600'
            }`}
          />
          {status === 'Connected' && (
            <p className="text-[10px] text-emerald-500/60">⚡ Трассировка недоступна. Добавьте кабель в «Коммутации» для полной интеграции.</p>
          )}
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button
          onClick={() => save()}
          disabled={saving || !!port.connected_cable_id}
          className="flex-1 bg-cyan-700 hover:bg-cyan-600 text-white rounded py-1 transition disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? '...' : 'Сохранить'}
        </button>
        <button
          onClick={() => { onTrace(port.id); onClose(); }}
          className="px-2 bg-indigo-900/60 hover:bg-indigo-700 text-indigo-300 rounded transition"
          title="Trace"
        >
          ↗
        </button>
      </div>
    </div>
  );
}

// --- Main component ---

export function RackPortsTab({
  selectedEq,
  ports,
  onTrace,
  onPortStatusChange,
  highlightPortId,
}: RackPortsTabProps) {
  const [openPortId, setOpenPortId] = useState<string | number | null>(null);
  // For focus restoration after overlay closes (Fix #14)
  const triggerRefs = useRef<Record<string | number, HTMLButtonElement | null>>({});

  if (!selectedEq) return null;

  const grouped = {
    data:  ports.filter(p => getPortCategory(p) === 'data'),
    mgmt:  ports.filter(p => getPortCategory(p) === 'mgmt'),
    power: ports.filter(p => getPortCategory(p) === 'power'),
  };

  const usedCount = ports.filter(p => p.status === 'Connected' || p.connected_cable_id).length;
  const freeCount = ports.length - usedCount;

  return (
    <div className="space-y-4">
      {/* Device summary pill */}
      <div className="flex items-center gap-2 p-2 bg-slate-900 border border-slate-800 rounded-lg text-xs">
        <span className="font-semibold text-slate-300 truncate">{selectedEq.name}</span>
        <span className="text-slate-600">·</span>
        <span className="text-slate-400 font-mono text-[10px]">#{selectedEq.id}</span>
      </div>

      <div className="bg-slate-950 border border-slate-800 p-3 rounded-lg">
        {/* Header */}
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-bold text-slate-200 uppercase text-xs">
            Порты ({ports.length})
          </h3>
          <div className="flex gap-2 text-[10px]">
            <span className="text-emerald-400">{usedCount} занято</span>
            <span className="text-slate-500">{freeCount} свободно</span>
          </div>
        </div>

        {/* Port groups */}
        {(['data', 'mgmt', 'power'] as const).map(cat => {
          const group = grouped[cat];
          if (group.length === 0) return null;
          const label = cat === 'data' ? 'Data' : cat === 'mgmt' ? 'Management' : 'Power';
          return (
            <div key={cat} className="mb-3">
              <div className="text-[9px] text-slate-600 uppercase mb-1 flex items-center gap-1">
                {CATEGORY_ICON[cat]} {label}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                {group.map(port => {
                  const portStatus = getPortStatus(port);
                  const isOpen = String(openPortId) === String(port.id);
                  const isHighlighted =
                    highlightPortId != null &&
                    String(highlightPortId).trim() === String(port.id).trim();

                  return (
                    <div key={port.id} className="relative">
                      <button
                        ref={el => { triggerRefs.current[port.id] = el; }}
                        onClick={() => setOpenPortId(isOpen ? null : port.id)}
                        aria-expanded={isOpen}
                        aria-haspopup="dialog"
                        className={`w-full p-2 rounded border flex flex-col gap-0.5 transition hover:brightness-125 text-left ${
                          isHighlighted
                            ? 'ring-2 ring-cyan-400 shadow-[0_0_12px_rgba(34,211,238,0.6)] border-cyan-400 bg-cyan-950/60'
                            : STATUS_STYLES[portStatus]
                        }`}
                      >
                        {/* Status dot */}
                        <div className="flex justify-between items-center gap-1">
                          <span className="font-bold text-[10px] min-w-0 flex-1 truncate">{port.name}</span>
                          {isHighlighted ? (
                            <span className="text-[9px] px-1 rounded bg-cyan-400 text-slate-950 font-black">
                              ★
                            </span>
                          ) : (
                            <Circle className={`w-2 h-2 flex-shrink-0 ${
                              portStatus === 'connected' ? 'fill-emerald-400 text-emerald-400' :
                              portStatus === 'reserved'  ? 'fill-amber-400  text-amber-400'  :
                              'fill-slate-700 text-slate-700'
                            }`} />
                          )}
                        </div>
                        <div className="flex items-center gap-0.5">
                          <span className="text-[8px] uppercase opacity-60 truncate">{port.port_type || 'RJ45'}</span>
                          <ChevronDown className="w-2 h-2 opacity-40 flex-shrink-0" />
                        </div>
                      </button>

                      {isOpen && (
                        <PortOverlay
                          port={port}
                          onClose={() => {
                            setOpenPortId(null);
                            // Restore focus to trigger button (Fix #14)
                            triggerRefs.current[port.id]?.focus();
                          }}
                          onSaved={() => { onPortStatusChange?.(); setOpenPortId(null); }}
                          onTrace={onTrace}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {ports.length === 0 && (
          <div className="text-center text-slate-500 py-6 text-xs">
            Нет портов у выбранного устройства.
          </div>
        )}
      </div>
    </div>
  );
}
